import { create } from "zustand";
import type { SessionUser } from "@spare-cart/contracts/auth";
import { apiClient, refreshSession, setAccessToken } from "@core/api";
import { unsubscribeWebPush } from "@core/push";
import { queryClient } from "@core/app/query-client";

export const SessionStatus = {
  RESTORING: "restoring",
  ANONYMOUS: "anonymous",
  AUTHENTICATED: "authenticated",
} as const;

export type SessionStatus = (typeof SessionStatus)[keyof typeof SessionStatus];

const RESTORE_RETRY_DELAYS_MS: readonly number[] = [0, 1000, 2000, 4000, 8000, 8000];

const sleep = (ms: number): Promise<void> =>
  ms === 0 ? Promise.resolve() : new Promise((resolve) => setTimeout(resolve, ms));

export type SessionState = {
  user: SessionUser | null;
  status: SessionStatus;
  justSignedUp: boolean;
  signIn: (user: SessionUser, accessToken: string, isNewUser?: boolean) => void;
  signOut: () => Promise<void>;
  restore: () => Promise<void>;
  acknowledgeSignUp: () => void;
};

export const useSession = create<SessionState>((set, get) => ({
  user: null,
  status: SessionStatus.RESTORING,
  justSignedUp: false,
  signIn: (user, accessToken, isNewUser = false) => {
    setAccessToken(accessToken);
    set({ user, status: SessionStatus.AUTHENTICATED, justSignedUp: isNewUser });
  },
  signOut: async () => {
    await unsubscribeWebPush().catch(() => undefined);
    try {
      await apiClient.post("/auth/logout", { audience: "customer" });
    } catch {
      // server session may already be gone; clear locally regardless
    }
    setAccessToken(null);
    queryClient.clear();
    set({ user: null, status: SessionStatus.ANONYMOUS, justSignedUp: false });
  },
  restore: async () => {
    for (const delay of RESTORE_RETRY_DELAYS_MS) {
      await sleep(delay);
      const outcome = await refreshSession();
      if (outcome.kind === "ok") {
        get().signIn(outcome.data.user, outcome.data.tokens.accessToken);
        return;
      }
      if (outcome.kind === "rejected") break;
    }
    setAccessToken(null);
    set({ user: null, status: SessionStatus.ANONYMOUS, justSignedUp: false });
  },
  acknowledgeSignUp: () => {
    set({ justSignedUp: false });
  },
}));

export const selectUser = (state: SessionState): SessionUser | null => state.user;

export const selectStatus = (state: SessionState): SessionStatus => state.status;

export const selectJustSignedUp = (state: SessionState): boolean => state.justSignedUp;

export const selectAcknowledgeSignUp = (state: SessionState): SessionState["acknowledgeSignUp"] =>
  state.acknowledgeSignUp;
